import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import type { SettingsSection } from "@/lib/types";

export type { SettingsSection };

export type SettingsNavGroup = {
  header: string;
  items: { id: SettingsSection; label: string; danger?: boolean }[];
};

/** Snapchat-style grouped list: section headers + rows with chevrons */
export const ADMIN_SETTINGS_GROUPS: SettingsNavGroup[] = [
  {
    header: "Business",
    items: [
      { id: "profile", label: "Business Profile" },
      { id: "billing", label: "Subscription & Billing" },
    ],
  },
  {
    header: "Alerts",
    items: [{ id: "notifications", label: "Notifications & Alerts" }],
  },
  {
    header: "Team",
    items: [
      { id: "team", label: "Team & Access" },
      { id: "permissions", label: "Permissions" },
    ],
  },
  {
    header: "Security & Privacy",
    items: [
      { id: "security", label: "Security" },
      { id: "appearance", label: "App Appearance" },
      { id: "support", label: "Support & Safety" },
    ],
  },
  {
    header: "Legal",
    items: [{ id: "legal", label: "Legal" }],
  },
  {
    header: "Account",
    items: [
      { id: "account", label: "My Account" },
      { id: "danger", label: "Danger Zone", danger: true },
    ],
  },
];

export const EMPLOYEE_SETTINGS_GROUPS: SettingsNavGroup[] = [
  {
    header: "My Settings",
    items: [
      { id: "account", label: "My Account" },
      { id: "notifications", label: "Notifications" },
      { id: "appearance", label: "App Appearance" },
    ],
  },
];

export function SettingsNav({
  active,
  onSelect,
  groups = ADMIN_SETTINGS_GROUPS,
}: {
  active: SettingsSection | null;
  onSelect: (s: SettingsSection) => void;
  groups?: SettingsNavGroup[];
}) {
  return (
    <nav className="w-full space-y-6">
      {groups.map((group) => (
        <div key={group.header}>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 px-1">
            {group.header}
          </p>
          <div className="rounded-lg border border-border bg-card overflow-hidden">
            {group.items.map((item, idx) => (
              <button
                key={item.id}
                type="button"
                onClick={() => onSelect(item.id)}
                className={cn(
                  "w-full flex items-center justify-between px-4 py-3.5 text-left text-[14px] transition-colors border-b border-border last:border-b-0 active:bg-muted/50",
                  active != null && active === item.id
                    ? "bg-primary/10 text-foreground font-medium"
                    : "text-foreground hover:bg-muted/30",
                  item.danger && active != null && active === item.id && "text-destructive",
                  item.danger && (active == null || active !== item.id) && "text-muted-foreground"
                )}
              >
                <span>{item.label}</span>
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
              </button>
            ))}
          </div>
        </div>
      ))}
    </nav>
  );
}
